import React, { useMemo } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { saveAs } from "file-saver";

import {
  buildExportFilename,
  buildOutcomeResponseLookup,
  buildResponseQuestionLookup,
  buildCsvContent,
  buildHeaderKeys,
  extractFollowUps,
  findOutcomeResponseValue,
  findQuestionResponseValue,
  flattenOutcomeQuestions,
  flattenSurveyQuestions,
  followUpsToColumns,
  normalizeCsvCell,
  normalizeCsvHeader,
  resolveExportDefinitions,
} from "./utils";

const ExportCsvButton = () => {
  const get_auth = useSelector((state) => state.auth);
  const raw_data = useSelector(
    (state) => state.audit?.raw_data,
    shallowEqual
  );
  const core_data = useSelector((state) => state.audit?.core_data);
  const get_culture_audit_reports = useSelector(
    (state) => state.debrief_schedule?.culture_audit_reports
  );
  const get_organizations = useSelector((state) => state.organizations);
  const get_survey_questions = useSelector((state) => state.surveyquestions);
  const get_employees = useSelector((state) => state.employees);

  const isAdmin = get_employees?.userEmp?.account_type === 5;

  const organizationData = useMemo(() => {
    if (!get_organizations) {
      return null;
    }

    return (
      get_organizations[
        get_auth?.organization_id || get_organizations?.organization?.id
      ] || null
    );
  }, [get_organizations, get_auth?.organization_id]);

  const surveyDefinitions = get_survey_questions?.survey_questions || [];
  const outcomeDefinitions = get_survey_questions?.outcome_questions || [];
  const categoryOrder = core_data?.categories?.[0]?.categories || [];

  const emailByEmployeeId = useMemo(() => {
    const mapping = {};
    (get_employees?.employees || []).forEach((employee) => {
      if (employee?.id) {
        mapping[employee.id] = employee?.email || "";
      }
    });
    return mapping;
  }, [get_employees?.employees]);

  const lookUpCategory = (category) => {
    if (!core_data || !category) {
      return "";
    }

    if (category.priority === "primary") {
      const categoryOptions = core_data.categories?.[0]?.categories?.find(
        (item) => item.id === category.id
      )?.options;

      if (
        Array.isArray(categoryOptions) &&
        categoryOptions[0] &&
        "subfactors" in categoryOptions[0]
      ) {
        const subfactorOptions = categoryOptions?.find(
          (item) => item.id === category.response
        )?.subfactors;

        return (
          subfactorOptions?.find((item) => item.id === category.subfactor)
            ?.name || ""
        );
      }

      return (
        categoryOptions?.find((item) => item.id === category.response)?.name ||
        ""
      );
    }

    if (!category.response) {
      return "";
    }

    const categoryOptions = core_data.categories?.[0]?.categories?.find(
      (item) => item.id === category.id
    )?.options;

    return (
      categoryOptions?.find((item) => item.id === category.response)?.name ||
      ""
    );
  };

  const getExportReferences = (surveyEntry, options = {}) => {
    const { allowCoreFallbacks = false } = options;
    const resolvedDefinitions = resolveExportDefinitions({
      surveyEntry,
      surveyDefinitions,
      outcomeDefinitions,
      organizationData,
      fallbackQuestionStructure: allowCoreFallbacks ? core_data?.questions : null,
      fallbackOutcomeQuestions: allowCoreFallbacks
        ? core_data?.outcomeQuestions
        : null,
    });

    return {
      ...resolvedDefinitions,
      orderedQuestions: flattenSurveyQuestions(
        resolvedDefinitions.questionStructure
      ),
      flattenedOutcomeQuestions: flattenOutcomeQuestions(
        resolvedDefinitions.outcomeQuestions
      ),
    };
  };

  const buildCommentColumns = (comments, commentDefinitions) => {
    const mappedComments = {};

    (comments || []).forEach((comment, index) => {
      const commentLabel = normalizeCsvHeader(
        commentDefinitions?.[index]?.question ||
          commentDefinitions?.[index]?.q ||
          `Comment ${index + 1}`
      );

      mappedComments[commentLabel] = normalizeCsvCell(
        comment?.feedback ?? comment?.comment ?? ""
      );
    });

    return mappedComments;
  };

  const buildCategoryColumns = (response) => {
    const mappedCategories = {};

    categoryOrder.forEach((coreCategory, index) => {
      const matchedCategory = response?.categories?.find(
        (category) => category?.id === coreCategory.id
      );

      mappedCategories[`Category ${index + 1}`] = matchedCategory
        ? lookUpCategory(matchedCategory)
        : "";
    });

    return mappedCategories;
  };

  const buildCsvRow = (responseEntry, exportReferences) => {
    const response = responseEntry?.response || responseEntry || {};
    const employeeId =
      responseEntry?.employee ||
      response?.employee ||
      responseEntry?.employee_id ||
      response?.employee_id;

    const questionResponseLookup = buildResponseQuestionLookup(
      response?.questions
    );
    const scores = {};
    exportReferences.orderedQuestions.forEach((questionDefinition) => {
      if (!questionDefinition?.header) {
        return;
      }

      scores[questionDefinition.header] = findQuestionResponseValue(
        questionResponseLookup,
        questionDefinition
      );
    });

    const outcomeResponseLookup = buildOutcomeResponseLookup(
      response?.employee_outcomes?.responses
    );
    const outcomes = {};
    exportReferences.flattenedOutcomeQuestions.forEach((outcomeQuestion) => {
      if (!outcomeQuestion?.header) {
        return;
      }

      outcomes[outcomeQuestion.header] = findOutcomeResponseValue(
        outcomeResponseLookup,
        outcomeQuestion
      );
    });

    const comments = buildCommentColumns(
      response?.comments,
      exportReferences.questionStructure?.comments
    );
    const followUps = followUpsToColumns(extractFollowUps(response?.feedback));

    return {
      "Member Email": employeeId ? emailByEmployeeId?.[employeeId] || "" : "",
      ...buildCategoryColumns(response),
      ...scores,
      ...outcomes,
      ...comments,
      ...followUps,
    };
  };

  const latestExportReferences = useMemo(() => {
    const latestSurvey = Array.isArray(raw_data) ? raw_data?.[0] : raw_data;
    return getExportReferences(latestSurvey, { allowCoreFallbacks: true });
  }, [
    raw_data,
    surveyDefinitions,
    outcomeDefinitions,
    organizationData,
    core_data,
  ]);

  const latestExportFilename = useMemo(
    () =>
      buildExportFilename({
        baseName: "raw_scores",
        surveySortOrder: latestExportReferences?.surveySortOrder,
        outcomeSortOrder: latestExportReferences?.outcomeSortOrder,
      }),
    [latestExportReferences]
  );

  const csvData = useMemo(() => {
    if (!raw_data || !core_data) {
      return [];
    }

    const latestResponses = Array.isArray(raw_data)
      ? raw_data?.[0]?.responses
      : raw_data?.responses;

    if (!Array.isArray(latestResponses)) {
      return [];
    }

    return latestResponses.map((responseEntry) =>
      buildCsvRow(responseEntry, latestExportReferences)
    );
  }, [raw_data, core_data, latestExportReferences, emailByEmployeeId]);

  if (!core_data || !raw_data) {
    return <div>Loading data...</div>;
  }

  if (!isAdmin) {
    return <div>Access restricted.</div>;
  }

  const buildCsvFromSurvey = (survey) => {
    if (!survey || !core_data) {
      return [];
    }

    const exportReferences = getExportReferences(survey);

    return (survey.responses || []).map((responseEntry) =>
      buildCsvRow(responseEntry, exportReferences)
    );
  };

  const downloadCsv = (data, filename) => {
    if (!Array.isArray(data) || data.length === 0) {
      return;
    }

    const headerKeys = buildHeaderKeys(data);
    const csvContent = buildCsvContent(data, headerKeys);
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    saveAs(blob, filename);
  };

  const surveyList = get_culture_audit_reports?.response || [];

  return (
    <div style={{ marginTop: 30 }}>
      <button
        type="button"
        onClick={() => downloadCsv(csvData, latestExportFilename)}
      >
        Download latest CSV
      </button>

      <div style={{ marginTop: 20 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th
                style={{
                  textAlign: "left",
                  borderBottom: "1px solid #ddd",
                  padding: "8px",
                }}
              >
                Name
              </th>
              <th
                style={{
                  textAlign: "left",
                  borderBottom: "1px solid #ddd",
                  padding: "8px",
                }}
              >
                Date
              </th>
              <th
                style={{
                  textAlign: "left",
                  borderBottom: "1px solid #ddd",
                  padding: "8px",
                }}
              >
                Version
              </th>
              <th
                style={{
                  textAlign: "left",
                  borderBottom: "1px solid #ddd",
                  padding: "8px",
                }}
              >
                Responses
              </th>
              <th
                style={{
                  textAlign: "left",
                  borderBottom: "1px solid #ddd",
                  padding: "8px",
                }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {surveyList.map((survey, index) => {
              const exportReferences = getExportReferences(survey);
              const data = buildCsvFromSurvey(survey);
              const filename = buildExportFilename({
                baseName: "raw_scores",
                surveySortOrder: exportReferences?.surveySortOrder,
                outcomeSortOrder: exportReferences?.outcomeSortOrder,
                suffix: `response-${survey?.id || index}`,
              });
              const dateString = survey?.date || survey?.created_at || "";
              const version = survey?.question?.sort_order || "";
              const count = survey?.responses?.length || 0;
              const name =
                survey?.name ||
                survey?.report_name ||
                survey?.question?.title ||
                survey?.question?.questions?.title ||
                survey?.type ||
                `Response ${survey?.id || index}`;

              return (
                <tr key={survey?.id || index}>
                  <td
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      padding: "8px",
                    }}
                  >
                    {name}
                  </td>
                  <td
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      padding: "8px",
                    }}
                  >
                    {dateString}
                  </td>
                  <td
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      padding: "8px",
                    }}
                  >
                    {version}
                  </td>
                  <td
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      padding: "8px",
                    }}
                  >
                    {count}
                  </td>
                  <td
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      padding: "8px",
                    }}
                  >
                    {data && data.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => downloadCsv(data, filename)}
                      >
                        Download CSV
                      </button>
                    ) : (
                      <span style={{ color: "#888" }}>No data</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExportCsvButton;
